'use client'

import React from 'react'
import { BookOpen } from 'lucide-react'

interface BlogSeriesNavProps {
  seriesName: string
  currentPart: number
  parts: {
    title: string
    href?: string
    description?: string
  }[]
}

export default function BlogSeriesNav({
  seriesName,
  currentPart,
  parts,
}: BlogSeriesNavProps) {
  return (
    <div data-blog-layout='narrow'>
      <nav
        aria-label={`${seriesName} series navigation`}
        className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
      >
        <div className='flex items-center gap-2 mb-5'>
          <BookOpen className='w-4 h-4 text-muted-foreground' />
          <span className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
            {seriesName}
          </span>
        </div>

        <div className='space-y-2'>
          {parts.map((part, index) => {
            const partNumber = index + 1
            const isCurrent = partNumber === currentPart

            return (
              <div
                key={index}
                className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
                  isCurrent
                    ? 'bg-[#008dcc]/5 border border-[#008dcc]/20'
                    : 'bg-background border border-border/60'
                }`}
              >
                <span
                  className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                    isCurrent ? 'text-white' : 'bg-muted text-muted-foreground'
                  }`}
                  style={isCurrent ? { backgroundColor: '#008dcc' } : undefined}
                >
                  {partNumber}
                </span>
                <div className='min-w-0'>
                  {isCurrent ? (
                    <span className='text-sm font-semibold text-foreground'>
                      {part.title}
                      <span className='ml-2 text-xs font-normal text-muted-foreground'>
                        (you are here)
                      </span>
                    </span>
                  ) : part.href ? (
                    <a
                      href={part.href}
                      className='text-sm font-medium text-[#008dcc] hover:underline underline-offset-2'
                    >
                      {part.title}
                    </a>
                  ) : (
                    <span className='text-sm font-medium text-muted-foreground'>
                      {part.title}
                      <span className='ml-2 text-xs'>(coming soon)</span>
                    </span>
                  )}
                  {part.description && (
                    <p className='text-xs text-muted-foreground mt-0.5'>
                      {part.description}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
